import React, {useCallback, useEffect, useState, Fragment, useContext} from 'react';
import {compose} from 'recompose';
import block from 'bem-cn-lite';
import {Dialog} from '@yandex-data-ui/common';
import {ServicePublic} from '@yandex-data-ui/cloud-schemas/build/models/ui-api';

import {removeHash} from 'utils';

import {BlockHeader} from '@yandex-data-ui/page-constructor';
import {isServiceNeedAccess} from 'units/constructor-local/utils';
import withRouter, {WithRouterProps} from 'hoc/withRouter';
import {ModalFormType, parseFormHash} from './utils';
import withFormsData, {WithFormsDataProps} from 'hoc/withFormsData';
import withFeatureFlags, {WithFeatureFlagsProps} from 'hoc/withFeatureFlags';
import MobileContext from 'contexts/MobileContext';
import YandexForm from 'components/YandexForm/YandexForm';

import './ModalForm.scss';

const PreviewAccessForm = React.lazy(
    () =>
        import(
            /* webpackChunkName: "PreviewAccessForm" */
            'components/forms/PreviewAccessForm/PreviewAccessForm'
        ),
);

const ContactSales = React.lazy(
    () =>
        import(
            /* webpackChunkName: "ContactSalesForm" */
            'units/constructor-local/forms/ContactSales/ContactSales'
        ),
);

const PartnerCommunication = React.lazy(
    () =>
        import(
            /* webpackChunkName: "PartnerCommunicationForm" */
            'units/constructor-local/forms/PartnerCommunication/PartnerCommunication'
        ),
);

const InterconnectAccessForm = React.lazy(
    () =>
        import(
            /* webpackChunkName: "InterconnectAccessForm" */
            'components/forms/InterconnectAccessForm/InterconnectAccessForm'
        ),
);

export interface ModalFormProps {
    service?: ServicePublic;
    className?: string;
}

export type ModalFormInnerProps = ModalFormProps &
    WithRouterProps &
    WithFormsDataProps &
    WithFeatureFlagsProps;

const b = block('modal-form-block');

const ModalForm: React.FC<ModalFormInnerProps> = (props) => {
    const {router, service, className, formsData, featureFlags} = props;
    const [type, setType] = useState<ModalFormType | null>(null);
    const [contentLoaded, setContentLoaded] = useState(false);
    const {type: formType, param, formData} = parseFormHash(router.hash, formsData);
    const isMobile = useContext(MobileContext);

    const onContentLoad = useCallback(() => {
        setContentLoaded(true);
    }, []);

    const onClose = useCallback(() => {
        removeHash(router);
        setType(null);
    }, [router]);

    useEffect(() => {
        setType(formType);
    }, [formType]);

    const renderForm = () => {
        if (type !== ModalFormType.Form && !contentLoaded) {
            onContentLoad();
        }

        switch (type) {
            case ModalFormType.Preview: {
                return service ? (
                    <PreviewAccessForm
                        service={service.slug}
                        serviceName={service.name}
                        useCustomHeader={!isServiceNeedAccess(service)}
                    />
                ) : null;
            }
            case ModalFormType.ContactSales:
                return <ContactSales />;
            case ModalFormType.PartnerCommunication:
                return <PartnerCommunication partnerId={param} />;
            case ModalFormType.Interconnect: {
                return <InterconnectAccessForm />;
            }
            case ModalFormType.Form: {
                return (
                    formData && (
                        <Fragment>
                            {formData.title && (
                                <BlockHeader
                                    title={{text: formData.title, textSize: 's'}}
                                    className={b('form-title', {isMobile: isMobile})}
                                    colSizes={{all: 12}}
                                />
                            )}
                            <YandexForm
                                id={formData.hash}
                                className={b('form')}
                                onLoad={onContentLoad}
                                containerId={'yandex-form-modal'}
                            />
                        </Fragment>
                    )
                );
            }
            default:
                return null;
        }
    };

    return (type && type !== ModalFormType.Form) ||
        (type === ModalFormType.Form && featureFlags.yandexForms) ? (
        <Dialog
            open={Boolean(type)}
            onClose={onClose}
            className={b({hidden: !contentLoaded}, className)}
            hasCloseButton
        >
            <Dialog.Body>
                <React.Suspense fallback>{renderForm()}</React.Suspense>
            </Dialog.Body>
        </Dialog>
    ) : null;
};

export default compose<ModalFormInnerProps, ModalFormProps>(
    withRouter,
    withFormsData,
    withFeatureFlags,
)(ModalForm);
